/**
 * client/render/entities.ts — everything that moves: players, missiles/pickups, fx, the dynamic light.
 * OWNER: RENDER agent. Game events -> fx mapping (GameEvent, shared/types.ts):
 *   EXPLOSION       -> fx.explosion (+ dynamic light)            explosive_bolt_mp blast
 *   IMPACT          -> fx.impact by surface                      prj_bolt_impact_<surface> fx stand-in
 *   MISSILE_STICK   -> blood if stuck in a player, else impact
 *   MISSILE_BOUNCE  -> small impact                              hatchet bounce
 *   MELEE_HIT       -> blood at the victim's chest               impacts/fx_flesh_hit_knife_mp
 *   SPLASH          -> water splash                              mp_hijacked.gsc splash
 *   ent WEAPON_FIRE -> muzzle flash at the world model tag_flash fx_crossbow_base (+ small light)
 */
import * as THREE from 'three';
import { GameEventType } from '@shared/types';
import type { ClientWorld } from '../types';
import { codVec } from './codxform';
import { FxSystem } from './fx';
import type { Bo2Uniforms } from './glsl';
import { MissileRenderer } from './missiles';
import { ModelCache } from './modelcache';
import { PlayerRenderer, ProbeGrid } from './players';
import type { SunShadows } from './shadows';
import type { LightProbe } from './world';

export class EntityRenderer {
  screenFlash = 0;
  private readonly cache = new ModelCache();
  readonly players: PlayerRenderer;
  readonly missiles: MissileRenderer;
  readonly fx = new FxSystem();
  private probes: ProbeGrid | null = null;
  private readonly v = new THREE.Vector3();
  private readonly n = new THREE.Vector3();
  private loaded = false;

  constructor(r: THREE.WebGLRenderer, private readonly U: Bo2Uniforms, scene: THREE.Scene, shadows: SunShadows, private readonly probeList: LightProbe[]) {
    this.players = new PlayerRenderer(this.cache, r, U, scene, shadows.dynamicScene);
    this.missiles = new MissileRenderer(this.cache, U, scene, this.fx);
    scene.add(this.fx.mesh);
  }

  async load(onProgress?: (f: number, l: string) => void): Promise<void> {
    this.probes = new ProbeGrid(this.probeList);
    await Promise.all([this.players.load(this.probeList, onProgress), this.missiles.load()]);
    this.loaded = true;
  }

  /**
   * Load-time warm-up objects (players with every weapon, every projectile model, a few particles) placed
   * around `at` (three space). Returns a toggle: the renderer shows them for its warm-up render, then hides
   * them for good (they stay in the scene so their programs are kept).
   */
  prewarm(at: THREE.Vector3): (show: boolean) => void {
    const p = this.players.prewarm(at);
    const m = this.missiles.prewarm(at);
    return (show) => { p(show); m(show); };
  }

  /** Returns the number of dynamic shadow casters this frame. */
  update(world: ClientWorld, camera: THREE.Camera, dtMs: number): number {
    if (!this.loaded) return 0;
    for (const fe of world.frameEvents) if (fe.src === 'game') this.onGameEvent(world, fe.ge);
    const casters = this.players.update(world, camera, dtMs);
    for (const p of this.players.flashes) {
      this.fx.muzzle(p);
      this.fx.light(p, [6, 4.5, 2.5], 160, 0.06);
    }
    this.missiles.update(world, dtMs, this.probes);
    this.fx.update(Math.min(0.1, dtMs / 1000), camera);
    this.updateDynLight();
    return casters;
  }

  private onGameEvent(world: ClientWorld, ge: import('@shared/types').GameEvent): void {
    switch (ge.type) {
      case GameEventType.EXPLOSION:
        this.fx.explosion(codVec(this.v, ge.pos));
        break;
      case GameEventType.IMPACT:
        this.fx.impact(codVec(this.v, ge.pos), codVec(this.n, ge.normal), ge.surface);
        break;
      case GameEventType.MISSILE_STICK: {
        codVec(this.v, ge.pos);
        if (ge.stuckTo >= 0 && ge.stuckTo < 0xff && world.getPlayer(ge.stuckTo)) this.fx.blood(this.v, codVec(this.n, ge.normal).negate(), 0.8);
        else this.fx.impact(this.v, codVec(this.n, ge.normal), ge.surface);
        break;
      }
      case GameEventType.MISSILE_BOUNCE:
        this.fx.impact(codVec(this.v, ge.pos), this.n.set(0, 1, 0), ge.surface, 0.5);
        break;
      case GameEventType.MELEE_HIT: {
        const victim = world.getPlayer(ge.victim);
        if (!victim) break;
        codVec(this.v, victim.origin);
        this.v.y += victim.stance === 2 ? 10 : victim.stance === 1 ? 30 : 48;
        const att = world.getPlayer(ge.attacker);
        const dir = att ? codVec(this.n, victim.origin).sub(codVec(new THREE.Vector3(), att.origin)).setY(0).normalize() : null;
        this.fx.blood(this.v, dir, 1);
        break;
      }
      case GameEventType.SPLASH:
        this.fx.splash(codVec(this.v, ge.pos), 1);
        break;
      default:
        break;
    }
  }

  /** Strongest live dynamic light -> shared uniforms (linear fade over its life). */
  private updateDynLight(): void {
    let best: (typeof this.fx.lights)[number] | null = null;
    let bestI = 0;
    for (const l of this.fx.lights) {
      const k = 1 - l.age / l.life;
      const i = (l.color.x + l.color.y + l.color.z) * k;
      if (i > bestI) { bestI = i; best = l; }
    }
    if (!best) { this.U.uDynLightPos.value.w = 0; return; }
    const k = 1 - best.age / best.life;
    this.U.uDynLightPos.value.set(best.pos.x, best.pos.y, best.pos.z, best.radius);
    this.U.uDynLightColor.value.copy(best.color).multiplyScalar(k);
  }

  setOption(name: string, value: number | boolean | string): void {
    if (name === 'testExplosion' && Array.isArray(value)) this.fx.explosion(new THREE.Vector3(...(value as number[])));
  }

  /** Debug/test helpers (window.__render.fx). */
  debugFx(kind: string, threePos: number[], surface = 0): void {
    const p = new THREE.Vector3(threePos[0], threePos[1], threePos[2]);
    if (kind === 'explosion') this.fx.explosion(p);
    else if (kind === 'blood') this.fx.blood(p, null);
    else if (kind === 'splash') this.fx.splash(p);
    else if (kind === 'impact') this.fx.impact(p, new THREE.Vector3(0, 1, 0), surface);
  }

  stats(): Record<string, unknown> {
    return { particles: this.fx.count, missiles: this.missiles.count, playersVisible: this.players.visibleCount, players: this.players.debug() };
  }

  dispose(): void {
    this.players.dispose();
    this.missiles.dispose();
    this.fx.dispose();
  }
}
